<template>
    <div class="alert-payload-details">
        <!-- Header.
             Shows the alert TYPE only, once. Both consumers (Activity.vue and
             DeviceAlertList.vue) render this panel inside a dialog whose
             v-card-title already shows notification.title, and this header used
             to repeat that title AND print alertTypeName twice - as a caption
             and again as a chip. A battery alert therefore opened with four
             near-identical headings before any actual detail. The coloured left
             border and icon carry the severity, so the chip is redundant too. -->
        <div class="d-flex align-center ga-3 pa-3 alert-header" :style="{ borderLeft: `4px solid ${alertColor}` }">
            <v-icon :icon="alertIcon" :color="alertColor" size="32"/>
            <div class="font-weight-bold text-body-1">{{ alertTypeName }}</div>
            <v-spacer/>
        </div>

        <!-- Quick summary banner -->
        <div v-if="summaryText" class="d-flex align-center ga-2 pa-2 px-3 summary-banner" :style="{ background: alertColor + '15', borderLeft: `3px solid ${alertColor}` }">
            <v-icon :icon="summaryIcon" :color="alertColor" size="18"/>
            <span class="text-body-2 font-weight-bold" :style="{ color: alertColor }">{{ summaryText }}</span>
        </div>

        <!-- Alert Info -->
        <v-table density="compact" class="info-table my-2">
            <tbody>
                <tr>
                    <td class="font-weight-bold" width="140">
                        <v-icon icon="mdi-car" size="small" class="me-1"/>Asset
                    </td>
                    <td>{{ notification.asset }}</td>
                </tr>
                <tr v-if="deviceSerial">
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-router-wireless" size="small" class="me-1"/>Device
                    </td>
                    <td>{{ deviceSerial }}</td>
                </tr>
                <tr>
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-clock-outline" size="small" class="me-1"/>Time
                    </td>
                    <td>{{ formatTimestamp(notification.event_timestamp) }}</td>
                </tr>
                <tr v-if="notification.monitoring_config_name">
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-cog" size="small" class="me-1"/>Config
                    </td>
                    <td>{{ notification.monitoring_config_name }}</td>
                </tr>
                <tr v-if="templateLabel">
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-tag" size="small" class="me-1"/>Rule
                    </td>
                    <td>{{ templateLabel }}</td>
                </tr>
            </tbody>
        </v-table>

        <!-- Speed Alert Details -->
        <div v-if="alertType === ALERT_TYPE.SPEED && payload" class="payload-section" style="border-color: #F44336;">
            <div class="payload-title" style="color: #F44336;">
                <v-icon icon="mdi-speedometer" size="small" class="me-1"/>Speed Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.speed_threshold">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-speedometer-slow" size="small" class="me-1" color="grey"/>Speed Limit
                        </td>
                        <td>{{ strOperations().unitifyDistance(payload.speed_threshold) }}</td>
                    </tr>
                    <tr v-if="actualSpeed">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-speedometer" size="small" class="me-1" color="red"/>Actual Speed
                        </td>
                        <td class="text-red font-weight-bold">{{ strOperations().unitifyDistance(actualSpeed) }}</td>
                    </tr>
                    <tr v-if="actualSpeed && payload.speed_threshold">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-arrow-up-bold" size="small" class="me-1" color="red"/>Over By
                        </td>
                        <td class="text-red">{{ strOperations().unitifyDistance(actualSpeed - payload.speed_threshold) }}</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Geofence Alert Details -->
        <div v-if="alertType === ALERT_TYPE.GEOFENCE && payload" class="payload-section" style="border-color: #FF9800;">
            <div class="payload-title" style="color: #FF9800;">
                <v-icon icon="mdi-map-marker-radius" size="small" class="me-1"/>Geofence Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="geofenceName">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-fence" size="small" class="me-1" color="orange"/>Zone
                        </td>
                        <td>{{ geofenceName }}</td>
                    </tr>
                    <!-- enter_or_exit: 1 = entered, ANY other value = exited. Shown
                         for 0 too; hidden only when the key is missing. -->
                    <tr v-if="directionKnown">
                        <td class="font-weight-bold">
                            <v-icon :icon="entered ? 'mdi-login' : 'mdi-logout'" size="small" class="me-1"
                                    :color="entered ? 'green' : 'red'"/>
                            Action
                        </td>
                        <td :class="entered ? 'text-green' : 'text-red'" class="font-weight-bold">
                            {{ entered ? 'Entered Zone' : 'Exited Zone' }}
                        </td>
                    </tr>
                    <tr v-if="payload.radius">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-radius-outline" size="small" class="me-1" color="grey"/>Radius
                        </td>
                        <td>{{ payload.radius }} meters</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Temperature Alert Details -->
        <div v-if="alertType === ALERT_TYPE.TEMPERATURE && payload" class="payload-section" style="border-color: #2196F3;">
            <div class="payload-title" style="color: #2196F3;">
                <v-icon icon="mdi-thermometer" size="small" class="me-1"/>Temperature Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.current_temperature != null">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-thermometer" size="small" class="me-1" color="red"/>Current
                        </td>
                        <td class="font-weight-bold">{{ payload.current_temperature }}&deg;C</td>
                    </tr>
                    <tr v-if="payload.low_threshold_celsius != null">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-thermometer-low" size="small" class="me-1" color="blue"/>Low Threshold
                        </td>
                        <td>{{ payload.low_threshold_celsius }}&deg;C</td>
                    </tr>
                    <tr v-if="payload.high_threshold_celsius != null">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-thermometer-high" size="small" class="me-1" color="red"/>High Threshold
                        </td>
                        <td>{{ payload.high_threshold_celsius }}&deg;C</td>
                    </tr>
                    <tr v-if="payload.current_temperature != null && (payload.low_threshold_celsius != null || payload.high_threshold_celsius != null)">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-alert" size="small" class="me-1" color="orange"/>Status
                        </td>
                        <td class="text-orange font-weight-bold">
                            {{ payload.current_temperature < payload.low_threshold_celsius ? 'Below Low Threshold' :
                               payload.current_temperature > payload.high_threshold_celsius ? 'Above High Threshold' : 'Out of Range' }}
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Light Sensor Alert Details -->
        <div v-if="alertType === ALERT_TYPE.LIGHT && payload" class="payload-section" style="border-color: #FFC107;">
            <div class="payload-title" style="color: #F9A825;">
                <v-icon icon="mdi-lightbulb-on" size="small" class="me-1"/>Light Sensor Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.lux != null">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-brightness-6" size="small" class="me-1" color="amber"/>Light Level
                        </td>
                        <td>{{ payload.lux }} lux</td>
                    </tr>
                    <tr v-if="payload.lux_int_type">
                        <td class="font-weight-bold">
                            <v-icon :icon="payload.lux_int_type === 'light_to_dark' ? 'mdi-weather-night' : 'mdi-white-balance-sunny'"
                                    size="small" class="me-1"
                                    :color="payload.lux_int_type === 'light_to_dark' ? 'indigo' : 'amber'"/>
                            Change Type
                        </td>
                        <td class="font-weight-bold">{{ formatLuxType(payload.lux_int_type) }}</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Push Button Alert Details -->
        <div v-if="alertType === ALERT_TYPE.PUSH_BUTTON && payload" class="payload-section" style="border-color: #9C27B0;">
            <div class="payload-title" style="color: #9C27B0;">
                <v-icon icon="mdi-gesture-tap-button" size="small" class="me-1"/>Push Button Alert
            </div>
            <v-table density="compact">
                <tbody>
                    <tr>
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-radiobox-marked" size="small" class="me-1" color="purple"/>Status
                        </td>
                        <td class="font-weight-bold">Button Pressed</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Type 13 carries three different things: a phone SOS and a tracker
             panic press (both real emergencies) and a hardware tamper/unplug,
             which is a "possible tampering" notice: no SOS styling, no 911. -->
        <div v-if="alertType === ALERT_TYPE.SOS" class="payload-section" :style="{ borderColor: alertColor }">
            <div class="payload-title" :style="{ color: alertColor }">
                <v-icon :icon="alertIcon" size="small" class="me-1"/>
                {{ isTamper ? 'Possible Tampering' : isPushButton ? 'Panic Button Pressed' : 'SOS Emergency' }}
            </div>
            <v-alert :type="isTamper ? 'warning' : 'error'" variant="tonal" density="compact" class="mb-2">
                {{ isTamper
                    ? 'The tracker lost power or was unplugged. It may have been tampered with, or its power source was disconnected.'
                    : isPushButton
                        ? 'The panic button was pressed on this tracker. Check on them immediately.'
                        : 'Emergency SOS was activated. Check on this person immediately.' }}
            </v-alert>
            <div class="d-flex flex-wrap ga-2">
                <v-btn v-if="!isDeviceSos && sosPhone" size="small" color="error" variant="outlined"
                       :href="`tel:${sosPhone}`" prepend-icon="mdi-phone">Call Him/Her</v-btn>
                <v-btn v-if="!isTamper" size="small" color="error" variant="outlined"
                       href="tel:911" prepend-icon="mdi-hospital-box">Call for Help</v-btn>
                <v-btn v-if="coords" size="small" :color="isTamper ? 'deep-orange' : 'error'" variant="outlined"
                       :href="`https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}`"
                       target="_blank" prepend-icon="mdi-directions">Directions</v-btn>
            </div>
        </div>

        <!-- No-Show Alert Details -->
        <div v-if="alertType === ALERT_TYPE.NO_SHOW && payload" class="payload-section" style="border-color: #E91E63;">
            <div class="payload-title" style="color: #E91E63;">
                <v-icon icon="mdi-clock-alert-outline" size="small" class="me-1"/>No-Show Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.place_name || payload.geofence_name || payload.location_name || payload.template_label">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-map-marker" size="small" class="me-1"/>Expected At
                        </td>
                        <td>{{ payload.place_name || payload.geofence_name || payload.location_name || payload.template_label }}</td>
                    </tr>
                    <tr>
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-information-outline" size="small" class="me-1"/>Status
                        </td>
                        <td class="font-weight-bold" style="color: #E91E63;">Did not arrive at expected location by check-in time</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Place Event Details -->
        <div v-if="alertType === ALERT_TYPE.PLACE && payload" class="payload-section" style="border-color: #4CAF50;">
            <div class="payload-title" style="color: #4CAF50;">
                <v-icon icon="mdi-map-marker-check" size="small" class="me-1"/>Place Event Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.location_name || payload.place_name || payload.template_label">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-map-marker" size="small" class="me-1"/>Location
                        </td>
                        <td>{{ payload.location_name || payload.place_name || payload.template_label }}</td>
                    </tr>
                    <tr v-if="directionKnown">
                        <td class="font-weight-bold">
                            <v-icon :icon="entered ? 'mdi-login' : 'mdi-logout'" size="small" class="me-1"
                                    :color="entered ? 'green' : 'orange'"/>Direction
                        </td>
                        <td class="font-weight-bold" :class="entered ? 'text-green' : 'text-orange'">
                            {{ entered ? 'Arrived at location' : 'Left location' }}
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Departure / Arrival Details -->
        <div v-if="alertType === ALERT_TYPE.DEPARTURE || alertType === ALERT_TYPE.ARRIVAL" class="payload-section"
             :style="{ borderColor: alertType === ALERT_TYPE.ARRIVAL ? '#009688' : '#3F51B5' }">
            <div class="payload-title" :style="{ color: alertType === ALERT_TYPE.ARRIVAL ? '#009688' : '#3F51B5' }">
                <v-icon :icon="alertType === ALERT_TYPE.ARRIVAL ? 'mdi-login-variant' : 'mdi-logout-variant'" size="small" class="me-1"/>
                {{ alertType === ALERT_TYPE.ARRIVAL ? 'Arrival' : 'Departure' }} Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload && (payload.location_name || payload.place_name || payload.template_label)">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-map-marker" size="small" class="me-1"/>Location
                        </td>
                        <td>{{ payload.location_name || payload.place_name || payload.template_label }}</td>
                    </tr>
                    <tr>
                        <td class="font-weight-bold">
                            <v-icon :icon="alertType === ALERT_TYPE.ARRIVAL ? 'mdi-login' : 'mdi-logout'" size="small" class="me-1"
                                    :color="alertType === ALERT_TYPE.ARRIVAL ? 'teal' : 'indigo'"/>Event
                        </td>
                        <td class="font-weight-bold" :class="alertType === ALERT_TYPE.ARRIVAL ? 'text-teal' : 'text-indigo'">
                            {{ alertType === ALERT_TYPE.ARRIVAL ? 'Arrived at location' : 'Left location' }}
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- System Alert Details (Battery, Crash, Maintenance). Excludes SOS/tamper
             (type 13): the SOS section above already owns it, and its device_unplugged
             sub_type would otherwise render an empty "Device Unplugged" table here. -->
        <div v-if="isSystemAlert && payload && alertType !== ALERT_TYPE.SOS" class="payload-section" :style="{ borderColor: systemAlertColor }">
            <div class="payload-title" :style="{ color: systemAlertColor }">
                <v-icon :icon="systemAlertIcon" size="small" class="me-1"/>
                {{ formatSubType(subTypeOf(payload)) }}
            </div>
            <v-table density="compact">
                <tbody>
                    <!-- Battery/Charger alerts. battery1_status/battery2_status
                         SELECT which channel the product reports (they are not
                         "this cell exists" flags), so the monitored rail is
                         shown first under the SKU's own name: on an OROBDREV23
                         that is "External Battery" (12V) with "Internal Backup"
                         (the 2.8V supercap) second, not the other way round. -->
                    <template v-if="isBatteryChargerAlert">
                        <tr v-if="primaryBattery">
                            <td class="font-weight-bold" width="140">
                                <v-icon icon="mdi-flash" size="small" class="me-1" color="orange"/>{{ primaryBatteryLabel }}
                            </td>
                            <td>{{ primaryBattery }}</td>
                        </tr>
                        <tr v-if="secondaryVoltage">
                            <td class="font-weight-bold">
                                <v-icon icon="mdi-flash-outline" size="small" class="me-1" color="orange"/>{{ secondaryBatteryLabel }}
                            </td>
                            <td>{{ (secondaryVoltage / 1000).toFixed(3) }}v</td>
                        </tr>
                    </template>

                    <!-- Maintenance alerts: DTC info -->
                    <template v-if="subTypeOf(payload) === SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED">
                        <tr v-if="payload.pending_dtcs">
                            <td class="font-weight-bold" width="140">
                                <v-icon icon="mdi-alert-circle" size="small" class="me-1" color="orange"/>Pending DTCs
                            </td>
                            <td class="font-weight-bold text-orange">{{ payload.pending_dtcs }}</td>
                        </tr>
                        <tr v-if="payload.indicator_status">
                            <td class="font-weight-bold">
                                <v-icon icon="mdi-engine" size="small" class="me-1" color="amber"/>MIL Lamp
                            </td>
                            <td>{{ payload.indicator_status }}</td>
                        </tr>
                    </template>

                    <!-- Crash alert -->
                    <template v-if="subTypeOf(payload) === SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION">
                        <tr>
                            <td class="font-weight-bold" width="140">
                                <v-icon icon="mdi-car-emergency" size="small" class="me-1" color="red"/>Status
                            </td>
                            <td class="font-weight-bold text-red">Crash Event Detected</td>
                        </tr>
                    </template>
                </tbody>
            </v-table>
        </div>

        <!-- DTC Details Section (for maintenance alerts) -->
        <div v-if="showDtcSection" class="payload-section" style="border-color: #FF9800;">
            <div class="payload-title" style="color: #FF9800;">
                <v-icon icon="mdi-car-wrench" size="small" class="me-1"/>Diagnostic Trouble Codes (DTC)
            </div>

            <!-- Loading state -->
            <div v-if="dtcLoading" class="d-flex align-center ga-2 pa-3">
                <v-progress-circular :size="18" :width="2" indeterminate color="orange"/>
                <span class="text-grey text-caption">Loading diagnostic codes...</span>
            </div>

            <!-- Error state -->
            <v-alert v-else-if="dtcError" density="compact" type="warning" variant="tonal" class="mb-0">
                {{ dtcError }}
            </v-alert>

            <!-- DTC data -->
            <template v-else-if="dtcData">
                <!-- Summary counts -->
                <div class="d-flex ga-3 mb-2 pa-2" style="background: rgba(var(--v-theme-warning), 0.12); border-radius: 4px;">
                    <div v-if="dtcData.no_of_stored_dtcs != null" class="text-center">
                        <div class="text-h6 text-red font-weight-bold">{{ dtcData.no_of_stored_dtcs || 0 }}</div>
                        <div class="text-caption">Stored</div>
                    </div>
                    <div v-if="dtcData.no_of_pending_dtcs != null" class="text-center">
                        <div class="text-h6 text-orange font-weight-bold">{{ dtcData.no_of_pending_dtcs || 0 }}</div>
                        <div class="text-caption">Pending</div>
                    </div>
                    <div v-if="dtcData.no_of_permanent_dtcs != null" class="text-center">
                        <div class="text-h6 text-deep-orange font-weight-bold">{{ dtcData.no_of_permanent_dtcs || 0 }}</div>
                        <div class="text-caption">Permanent</div>
                    </div>
                    <v-spacer/>
                    <div v-if="dtcData.event_datetime" class="text-caption text-grey align-self-center">
                        Reported: {{ formatTimestamp(dtcData.event_datetime) }}
                    </div>
                </div>

                <!-- Individual DTC codes -->
                <template v-if="dtcData.description">
                    <div v-for="(codes, typeName) in dtcData.description" :key="typeName">
                        <div class="text-caption font-weight-bold text-grey-darken-1 mb-1 mt-2">{{ typeName }}</div>
                        <div v-for="dtc in codes" :key="dtc.code" class="dtc-code-card mb-2">
                            <div class="d-flex align-center ga-2 mb-1">
                                <v-chip size="small" color="orange" variant="flat" class="font-weight-bold" style="font-family: monospace;">
                                    {{ dtc.code }}
                                </v-chip>
                                <span class="text-body-2 font-weight-bold text-grey-darken-2">{{ dtc.description }}</span>
                            </div>
                            <div v-if="dtc.user_description" class="d-flex align-start ga-1 mb-1 ps-1">
                                <v-icon icon="mdi-information" size="x-small" color="blue" class="mt-1"/>
                                <span class="text-caption text-blue-darken-1">{{ dtc.user_description }}</span>
                            </div>
                            <div v-if="dtc.suggested_fix" class="d-flex align-start ga-1 ps-1">
                                <v-icon icon="mdi-wrench" size="x-small" color="green" class="mt-1"/>
                                <span class="text-caption text-green-darken-1">{{ dtc.suggested_fix }}</span>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- No codes -->
                <div v-else-if="!dtcData.description || Object.keys(dtcData.description).length === 0" class="text-caption text-grey pa-2">
                    No diagnostic codes reported
                </div>
            </template>
        </div>

        <!-- Location Section -->
        <div v-if="hasLocation" class="payload-section" style="border-color: #4CAF50;">
            <div class="payload-title" style="color: #4CAF50;">
                <v-icon icon="mdi-map-marker" size="small" class="me-1"/>Reported Location
            </div>
            <v-table density="compact">
                <tbody>
                    <tr>
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-crosshairs-gps" size="small" class="me-1"/>Coordinates
                        </td>
                        <td class="d-flex align-center ga-1">
                            <span>{{ coords.lat }}, {{ coords.lng }}</span>
                            <v-tooltip location="top" text="Open in Google Maps">
                                <template v-slot:activator="{ props: tp }">
                                    <v-btn :href="`https://www.google.com/maps?q=${coords.lat},${coords.lng}`"
                                           target="_blank" icon="mdi-google-maps" size="x-small" variant="text"
                                           color="red" density="compact" v-bind="tp"/>
                                </template>
                            </v-tooltip>
                            <v-tooltip location="top" text="Copy coordinates">
                                <template v-slot:activator="{ props: tp }">
                                    <v-btn icon="mdi-content-copy" size="x-small" variant="text"
                                           color="grey" density="compact" v-bind="tp"
                                           @click="copyCoords"/>
                                </template>
                            </v-tooltip>
                        </td>
                    </tr>
                    <tr v-if="payload.location_name">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-office-building-marker" size="small" class="me-1"/>Location
                        </td>
                        <td>{{ payload.location_name }}</td>
                    </tr>
                    <tr v-if="displayAddress">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-map-search" size="small" class="me-1"/>Address
                        </td>
                        <td>
                            <span>{{ displayAddress }}</span>
                            <v-progress-circular v-if="geocoding" :size="12" :width="2" indeterminate color="grey" class="ms-2"/>
                        </td>
                    </tr>
                    <tr v-else-if="geocoding">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-map-search" size="small" class="me-1"/>Address
                        </td>
                        <td>
                            <v-progress-circular :size="12" :width="2" indeterminate color="grey" class="me-2"/>
                            <span class="text-grey text-caption">Looking up address...</span>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Fallback: a friendly key/value table for any payload that has no
             dedicated section above (instead of dumping raw JSON). -->
        <div v-if="showFallback && prettyRows.length" class="payload-section" style="border-color: #9E9E9E;">
            <div class="payload-title" style="color: #757575;">
                <v-icon icon="mdi-information-outline" size="small" class="me-1"/>Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-for="row in prettyRows" :key="row.k">
                        <td class="font-weight-bold" width="140">{{ row.label }}</td>
                        <td>{{ row.value }}</td>
                    </tr>
                </tbody>
            </v-table>
        </div>
    </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {mapConfig} from "@/composables/mapConfig";
import {send} from "@/composables/apiClient";
import {useClipboard} from "@/composables/clipboard";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {strOperations} from "@/composables/globals";
import {formatDateTime} from "@/composables/useTimezone";
import {
    ALERT_TYPE, SYSTEM_ALERT_SUB_TYPE, SYSTEM_ALERTS_TYPE, BATTERY_SUB_TYPES,
    alertTypeOf, subTypeOf, payloadOf, coordsOf, hasDirection, isEntered,
    alertFace, subTypeLabel,
    isSystemAlert as isSystemAlertRow, isTamper as isTamperAlert,
    isPushButton as isPushButtonAlert, isDeviceSos as isDeviceSosAlert,
} from "@/composables/alertTypes";

const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();

const props = defineProps({
    notification: {type: Object, required: true},
});

// The payload sometimes arrives as a JSON string; payloadOf parses it so both
// the typed sections and the generic table below can read its fields.
const payload = computed(() => payloadOf(props.notification));

// Keys already shown elsewhere (header/info/location) or that are internal noise -
// excluded from the friendly "Details" table so it stays readable for regular users.
const PAYLOAD_SKIP = new Set([
    'alert_type', 'type', 'sub_type', 'asset_id', 'device_id', 'company_id', 'id',
    'monitoring_config_id', 'monitoring_notification_group_id', 'template_id',
    'notification_id', 'user_id', 'portal_id', 'created_at', 'updated_at',
    'event_timestamp', 'timestamp', 'latitude', 'longitude', 'formatted_address',
    'location', 'location_name', 'template_label', 'geofence_name', 'device_slno',
    'slno', 'asset', 'read', 'is_read', 'mqtt_topic', 'mqtt_payload_key',
    'payload_key', 'token', 'gps_speed', 'imei', 'iccid',
    // SKU channel metadata - it drives which voltage is shown and what it is
    // called, but is not itself a fact a user wants as a row.
    'battery1_status', 'battery2_status', 'battery1_label', 'battery2_label',
    'is_phone',
]);
// Never surface credential-ish keys, even if a future payload adds one.
const SENSITIVE_KEY = /token|secret|passwo?rd|api[_-]?key|auth[_-]?key|access[_-]?key/i;
const PAYLOAD_LABELS = {
    voltage1: 'Battery Voltage', voltage2: 'External Voltage', current_speed: 'Speed',
    speed: 'Speed', speed_threshold: 'Speed Limit', current_temperature: 'Temperature',
    low_threshold_celsius: 'Low Threshold', high_threshold_celsius: 'High Threshold',
    lux: 'Light Level', lux_int_type: 'Light Change', enter_or_exit: 'Action',
    radius: 'Radius', pending_dtcs: 'Pending DTCs', indicator_status: 'Check Engine',
    gps_speed: 'GPS Speed', heading: 'Heading', satellites: 'Satellites',
    accuracy: 'GPS Accuracy', altitude: 'Altitude', battery_percent: 'Battery',
};
const humanizeKey = (k) =>
    PAYLOAD_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const humanizeVal = (k, v) => {
    if (v === null || v === undefined || v === '' || v === 'null') return null;
    if (typeof v === 'object') return null; // skip nested objects/arrays for a clean table
    if (k === 'enter_or_exit') return Number(v) === 1 ? 'Entered' : 'Exited';
    if ((k === 'voltage1' || k === 'voltage2') && !isNaN(v)) return (v / 1000).toFixed(2) + ' V';
    if (k === 'lux_int_type') return formatLuxType(v);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'string' && v.includes('_') && isNaN(v)) {
        return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return String(v);
};
const prettyRows = computed(() => {
    const p = payload.value;
    if (!p || typeof p !== 'object') return [];
    const rows = [];
    for (const [k, v] of Object.entries(p)) {
        if (PAYLOAD_SKIP.has(k) || SENSITIVE_KEY.test(k)) continue;
        const val = humanizeVal(k, v);
        if (val === null) continue;
        // Key on the raw payload key, not the display label - two keys can map to
        // the same label (e.g. speed/current_speed) and collide as v-for keys.
        rows.push({k, label: humanizeKey(k), value: val});
    }
    return rows;
});
const alertType = computed(() => {
    const fromRow = alertTypeOf(props.notification) ?? alertTypeOf(payload.value);
    if (fromRow !== null) return fromRow;
    // Fallback for rows that carry only the gateway's STRING type and no
    // numeric alert_type. system_alerts maps to MAINTENANCE here because that
    // is the API's own default arm when the sub_type is unrecognised.
    const typeMap = {
        geofence: ALERT_TYPE.GEOFENCE,
        speed: ALERT_TYPE.SPEED,
        light: ALERT_TYPE.LIGHT,
        push_button: ALERT_TYPE.PUSH_BUTTON,
        temperature: ALERT_TYPE.TEMPERATURE,
        [SYSTEM_ALERTS_TYPE]: ALERT_TYPE.MAINTENANCE,
    };
    return typeMap[payload.value?.type] || null;
});

// A hardware tamper/theft SOS (sub_type device_unplugged) vs a person's panic
// SOS - they read/behave differently (no human to call for a tamper alert).
const isTamper = computed(() => isTamperAlert(payload.value));
// A press of the tracker's own panic button: files as type 13 like a phone SOS
// and a tamper, but is neither. No initiator user, so nobody to call back.
const isPushButton = computed(() => isPushButtonAlert(payload.value));
const isDeviceSos = computed(() => isDeviceSosAlert(payload.value));
// The initiator's phone for "Call Him/Her" - server-provided (api #1215); null
// for a tamper alert (no human initiator) or when absent.
const sosPhone = computed(() => {
    const raw = payload.value?.initiator_phone;
    const s = raw == null ? '' : String(raw).trim();
    return s || null;
});
// Reported position. Hardware alerts carry latitude/longitude, a phone SOS
// only lat/lng; coordsOf reads either and treats '' as absent.
const coords = computed(() => coordsOf(payload.value));

// enter_or_exit: 1 = entered/arrived, anything else = left.
const directionKnown = computed(() => hasDirection(payload.value));
const entered = computed(() => isEntered(payload.value));

// Label / icon / colour from the shared helper, keyed on the RESOLVED type
// (including the string-type fallback above) plus sub_type. Never the API's
// alert_type_name, which is a generic bucket ("GENERAL ALERT", "SOS ALERT").
const face = computed(() => alertFace({alert_type: alertType.value, payload: payload.value}));
const alertTypeName = computed(() => face.value.label);
const alertIcon = computed(() => face.value.icon);
const alertColor = computed(() => face.value.color);

// Device-health family (battery/charger, crash, check engine). Type 13 is
// excluded by the section's own v-if.
const isSystemAlert = computed(() => {
    if (!payload.value) return false;
    return isSystemAlertRow({payload: payload.value}) || subTypeOf(payload.value) !== null ||
           [ALERT_TYPE.MAINTENANCE, ALERT_TYPE.BATTERY, ALERT_TYPE.CRASH]
               .includes(alertType.value);
});

const isBatteryChargerAlert = computed(() => BATTERY_SUB_TYPES.includes(subTypeOf(payload.value)));

// --- Alert voltage channels ---
//
// battery1_status / battery2_status do not mean "this cell exists" - they
// SELECT which channel the product reports, and exactly one is ever set. On an
// Orion OBD battery2 is the 12V vehicle rail and voltage1 is the ~2.8V
// supercap, so reading voltage1 blindly showed the supercap as "the battery"
// and an 11.48V low-battery alert rendered as 2.76v. Phones instead encode a
// PERCENT on voltage1 (percent = voltage1 / 50). Mirrors the precedence the
// live map (DevicesMap) and the API's buildBatteryPush already use.
const alertIsPhone = computed(() => payload.value?.is_phone === true || payload.value?.is_phone === 1);
const usesBattery2 = computed(
    () => payload.value?.battery2_status === true && payload.value?.battery1_status !== true
);

const primaryVoltage = computed(() => {
    const p = payload.value;
    if (!p) return null;
    const mv = (!alertIsPhone.value && usesBattery2.value) ? p.voltage2 : p.voltage1;
    return (mv === null || mv === undefined || isNaN(mv)) ? null : Number(mv);
});

const secondaryVoltage = computed(() => {
    const p = payload.value;
    if (!p || alertIsPhone.value) return null;
    const mv = usesBattery2.value ? p.voltage1 : p.voltage2;
    return (mv === null || mv === undefined || isNaN(mv)) ? null : Number(mv);
});

/// "12%" on a phone, "11.477v" on hardware.
const primaryBattery = computed(() => {
    const mv = primaryVoltage.value;
    if (mv === null) return null;
    if (alertIsPhone.value) return `${Math.max(0, Math.min(100, Math.round(mv / 50)))}%`;
    return `${(mv / 1000).toFixed(3)}v`;
});

// The SKU names its own channels (product_skus.battery1_label/battery2_label,
// sent on the alert payload). Nullable: not every SKU sets them, and alerts
// raised before the API sent them have no such key, so keep a fallback.
const channelLabel = (channel2) => {
    const raw = payload.value?.[channel2 ? 'battery2_label' : 'battery1_label'];
    const s = (raw ?? '').toString().trim();
    return s === '' ? null : s;
};

const primaryBatteryLabel = computed(() => {
    if (alertIsPhone.value) return channelLabel(false) || 'Battery';
    return channelLabel(usesBattery2.value) || 'Battery Voltage';
});

const secondaryBatteryLabel = computed(
    () => channelLabel(!usesBattery2.value) || (usesBattery2.value ? 'Internal Backup' : 'External Voltage')
);

// Types that already render a dedicated section above, so the generic
// key/value "Details" table would only repeat them. 6/7/8 are covered by
// isSystemAlert instead.
const TYPES_WITH_OWN_SECTION = [
    ALERT_TYPE.GEOFENCE, ALERT_TYPE.SPEED, ALERT_TYPE.LIGHT, ALERT_TYPE.PUSH_BUTTON,
    ALERT_TYPE.TEMPERATURE, ALERT_TYPE.NO_SHOW, ALERT_TYPE.PLACE,
    ALERT_TYPE.DEPARTURE, ALERT_TYPE.ARRIVAL, ALERT_TYPE.SOS,
];

const showFallback = computed(() => {
    return !TYPES_WITH_OWN_SECTION.includes(alertType.value) && !isSystemAlert.value;
});

// Section accent for the device-health block: the same face as the header.
const systemAlertIcon = alertIcon;
const systemAlertColor = alertColor;

const actualSpeed = computed(() => payload.value?.current_speed || payload.value?.speed || null);

const deviceSerial = computed(() =>
    payload.value?.device_slno || payload.value?.slno || props.notification?.device_slno || null
);

const geofenceName = computed(() =>
    payload.value?.template_label || props.notification?.template_label || null
);

const geofenceAction = computed(() => {
    if (!directionKnown.value) return null;
    return entered.value ? 'Entered' : 'Exited';
});

const summaryText = computed(() => {
    if (!payload.value) return null;
    if (alertType.value === ALERT_TYPE.SPEED) {
        const speed = actualSpeed.value;
        const limit = payload.value.speed_threshold;
        if (speed && limit) return `Speed: ${strOperations().unitifyDistance(speed)} (Limit: ${strOperations().unitifyDistance(limit)})`;
        if (speed) return `Speed: ${strOperations().unitifyDistance(speed)}`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.GEOFENCE) {
        const action = geofenceAction.value;
        const zone = geofenceName.value;
        if (action && zone) return `${action}: ${zone}`;
        if (action) return action;
        if (zone) return `Zone: ${zone}`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.TEMPERATURE) {
        const temp = payload.value.current_temperature;
        if (temp != null) return `Temperature: ${temp}°C`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.LIGHT) {
        const lux = payload.value.lux;
        const change = formatLuxType(payload.value.lux_int_type);
        if (lux != null && change) return `${change} (${lux} lux)`;
        if (lux != null) return `Light Level: ${lux} lux`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.PUSH_BUTTON) return 'Push Button Pressed';
    if (alertType.value === ALERT_TYPE.SOS) {
        if (isTamper.value) return 'Possible tampering: tracker lost power or was unplugged';
        if (isPushButton.value) return 'Panic button pressed';
        return 'SOS EMERGENCY triggered';
    }
    if (isSystemAlert.value) {
        const sub = formatSubType(subTypeOf(payload.value));
        // Quote the MONITORED channel (and a percent for phones), not voltage1,
        // which on an OBD tracker is the supercap rather than the rail the
        // alert is about.
        if (isBatteryChargerAlert.value && primaryBattery.value) {
            return `${sub} - ${primaryBattery.value}`;
        }
        return sub;
    }
    if (alertType.value === ALERT_TYPE.NO_SHOW) {
        const place = payload.value?.place_name || payload.value?.geofence_name
            || payload.value?.location_name || payload.value?.template_label;
        return place ? `Did not arrive at ${place}` : 'Tracker did not arrive at expected location';
    }
    if (alertType.value === ALERT_TYPE.PLACE) {
        const place = payload.value?.location_name || payload.value?.place_name || payload.value?.template_label;
        // Phrase by direction (enter_or_exit: 1 = arrived, anything else = left)
        // like the app, falling back to a neutral label when direction is absent.
        if (directionKnown.value) {
            if (place) return entered.value ? `Arrived at ${place}` : `Left ${place}`;
            return entered.value ? 'Arrived' : 'Left';
        }
        return place ? `Place event at ${place}` : 'Place event';
    }
    if (alertType.value === ALERT_TYPE.DEPARTURE) {
        const place = payload.value?.location_name || payload.value?.place_name || payload.value?.template_label;
        return place ? `Left ${place}` : 'Departed location';
    }
    if (alertType.value === ALERT_TYPE.ARRIVAL) {
        const place = payload.value?.location_name || payload.value?.place_name || payload.value?.template_label;
        return place ? `Arrived at ${place}` : 'Arrived at location';
    }
    return null;
});

const summaryIcon = computed(() => {
    if (alertType.value === ALERT_TYPE.SPEED) return 'mdi-speedometer';
    if (alertType.value === ALERT_TYPE.GEOFENCE) {
        if (!directionKnown.value) return 'mdi-map-marker-radius';
        return entered.value ? 'mdi-login' : 'mdi-logout';
    }
    if (alertType.value === ALERT_TYPE.TEMPERATURE) return 'mdi-thermometer';
    if (alertType.value === ALERT_TYPE.LIGHT) return 'mdi-lightbulb-on';
    if (alertType.value === ALERT_TYPE.PUSH_BUTTON) return 'mdi-gesture-tap-button';
    if (alertType.value === ALERT_TYPE.SOS) return alertIcon.value;
    if (isSystemAlert.value) return systemAlertIcon.value;
    if (alertType.value === ALERT_TYPE.NO_SHOW) return 'mdi-clock-alert-outline';
    if (alertType.value === ALERT_TYPE.PLACE) return 'mdi-map-marker-check';
    if (alertType.value === ALERT_TYPE.DEPARTURE) return 'mdi-logout-variant';
    if (alertType.value === ALERT_TYPE.ARRIVAL) return 'mdi-login-variant';
    return alertIcon.value;
});

const templateLabel = computed(() =>
    payload.value?.template_label || props.notification?.template_label || null
);

const hasLocation = computed(() => coords.value !== null);

// Reverse geocoding for address fallback
const geocodedAddress = ref(null);
const geocoding = ref(false);
let geocodeRequestId = 0;

const displayAddress = computed(() =>
    payload.value?.location || payload.value?.formatted_address || geocodedAddress.value || null
);

const reverseGeocode = (lat, lng) => {
    const requestId = ++geocodeRequestId;
    geocoding.value = true;
    fetch(`${mapConfig.reverseUrl}?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=0`)
        .then(res => res.json())
        .then(data => {
            if (requestId !== geocodeRequestId) return;
            if (data?.display_name) {
                geocodedAddress.value = data.display_name;
            }
        })
        .catch(() => {})
        .finally(() => {
            if (requestId === geocodeRequestId) geocoding.value = false;
        });
};

// Auto-geocode when location has coords but no address
watch(() => payload.value, (p) => {
    geocodedAddress.value = null;
    geocodeRequestId++;
    const c = coordsOf(p);
    if (c && !p.location && !p.formatted_address) {
        reverseGeocode(c.lat, c.lng);
    } else {
        geocoding.value = false;
    }
}, {immediate: true});

// DTC fetching for maintenance alerts
const dtcLoading = ref(false);
const dtcError = ref(null);
const dtcData = ref(null);
const showDtcSection = computed(() =>
    subTypeOf(payload.value) === SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED && props.notification?.asset_id
);

const fetchDtcs = () => {
    const assetId = props.notification?.asset_id || payload.value?.asset_id;
    if (!assetId) return;
    dtcLoading.value = true;
    dtcError.value = null;
    send('get', `assets/${assetId}/dtcs`)
        .then((response) => {
            dtcData.value = response.data.data;
        })
        .catch(() => {
            dtcError.value = 'Could not load diagnostic codes for this asset';
        })
        .finally(() => {
            dtcLoading.value = false;
        });
};

// Auto-fetch DTCs when maintenance alert is shown
watch(showDtcSection, (show) => {
    if (show && !dtcData.value && !dtcLoading.value) fetchDtcs();
}, {immediate: true});

const formatTimestamp = (ts) => {
    if (!ts) return '';
    return formatDateTime(ts);
};

// Section title / summary for the device-health block. With no sub_type, use
// the resolved type's label (type 6 reads "Maintenance / Check Engine", not "System Alert").
const formatSubType = (subType) => subTypeLabel(subType) || alertTypeName.value;

const formatLuxType = (type) => {
    if (!type) return '';
    const types = {
        'light_to_dark': 'Light to Dark',
        'dark_to_light': 'Dark to Light',
    };
    return types[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const copyCoords = () => {
    const c = coords.value;
    if (c) copyToClipboard(`${c.lat}, ${c.lng}`);
};
</script>

<style scoped>
.alert-payload-details {
    padding: 8px;
}
.alert-header {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 6px;
}
.summary-banner {
    border-radius: 4px;
    margin-top: 8px;
    font-weight: 600;
}
.info-table {
    background: transparent;
}
.info-table td {
    padding: 6px 12px !important;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
}
.payload-section {
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-left-width: 4px;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 10px;
}
.payload-title {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
}
.payload-section td {
    padding: 4px 8px !important;
}
.dtc-code-card {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-radius: 6px;
    padding: 8px 10px;
}
</style>
